<template>
  <modal
    :name="modalName" :width="680" :height="'auto'" :adaptive="true"
    :scrollable="true" :reset="true" class="mailgun-template-details modal-not-overflow"
    @opened="focusFirstField" @closed="$emit('close')"
  >
    <section role="dialog" aria-modal="true" aria-labelledby="mailgun-template-details-title">
      <AppModalHeader
        ref="header" design="new" eyebrow="Mailgun · Templates" :title="title"
        title-id="mailgun-template-details-title" class="mailgun-template-details__header"
        @close="close"
      />
      <div v-if="loading" class="mailgun-template-details__state"><AppSpinner design="new" /></div>
      <form v-else class="mailgun-template-details__form" @submit.prevent="save">
        <div class="mailgun-template-details__grid">
          <AppFormField design="new" label="Name" label-for="mailgun-template-name" required>
            <AppInput id="mailgun-template-name" ref="name" v-model="form.name" design="new" maxlength="255" placeholder="Template name" />
          </AppFormField>
          <AppFormField design="new" label="Code" label-for="mailgun-template-code" required>
            <AppInput id="mailgun-template-code" v-model="form.code" design="new" maxlength="255" placeholder="template_code" />
          </AppFormField>
          <AppFormField design="new" label="Mailgun template name" label-for="mailgun-remote-name" required>
            <AppInput id="mailgun-remote-name" v-model="form.remote_name" design="new" maxlength="255" placeholder="Name in Mailgun" />
          </AppFormField>
          <AppFormField design="new" label="Mailgun template ID" label-for="mailgun-remote-id" required>
            <AppInput id="mailgun-remote-id" v-model="form.remote_id" design="new" maxlength="255" placeholder="Mailgun ID" />
          </AppFormField>
        </div>
        <AppFormField design="new" label="Description" label-for="mailgun-template-description">
          <AppTextarea id="mailgun-template-description" v-model="form.description" design="new" maxlength="2000" placeholder="Where this template is used" />
        </AppFormField>
        <label class="mailgun-template-details__active">
          <span><strong>Active</strong><small>Available for live sending</small></span>
          <AppSwitcher v-model="form.active" design="new" aria-label="Template active" />
        </label>
        <p v-if="error" class="mailgun-template-details__error" role="alert">{{ error }}</p>
        <footer class="mailgun-template-details__footer">
          <AppButton design="new" theme="dark" type="button" :disabled="saving" @click.native="close">Cancel</AppButton>
          <AppButton design="new" theme="success" type="submit" :disabled="saving || !valid">{{ saving ? 'Saving…' : (isEdit ? 'Save changes' : 'Create template') }}</AppButton>
        </footer>
      </form>
    </section>
  </modal>
</template>

<script>
import {
  getSupportMailgunTemplate,
  patchSupportMailgunTemplateMetadata,
  postSupportMailgunTemplate
} from '@/http'

export default {
  name: 'MailgunTemplateDetailsModal',
  props: { templateId: { type: Number, default: null } },
  data() {
    return {
      modalName: `mailgun-template-details-${this.templateId || 'new'}`,
      form: { name: '', code: '', description: '', remote_id: '', remote_name: '', active: true },
      loading: Boolean(this.templateId),
      saving: false,
      error: ''
    }
  },
  computed: {
    isEdit() { return Boolean(this.templateId) },
    title() { return this.isEdit ? 'Edit template' : 'Create template' },
    valid() {
      return ['name', 'code', 'remote_id', 'remote_name'].every(key => String(this.form[key] || '').trim())
    }
  },
  mounted() { this.$modal.show(this.modalName); if (this.isEdit) this.load() },
  methods: {
    async load() {
      this.loading = true
      this.error = ''
      try {
        const { data } = await getSupportMailgunTemplate(this.templateId)
        this.form = {
          name: data.name || '',
          code: data.code || '',
          description: data.description || '',
          remote_id: data.binding?.remote_id || '',
          remote_name: data.binding?.remote_name || '',
          active: Boolean(data.active)
        }
        this.$nextTick(this.focusFirstField)
      } catch (error) {
        this.error = error.response?.data?.details || 'Unable to load this template.'
      } finally { this.loading = false }
    },
    async save() {
      if (!this.valid || this.saving) return
      this.saving = true
      this.error = ''
      try {
        const payload = {
          ...this.form,
          name: String(this.form.name || '').trim(),
          code: String(this.form.code || '').trim(),
          description: this.form.description || '',
          remote_id: String(this.form.remote_id || '').trim(),
          remote_name: String(this.form.remote_name || '').trim()
        }
        const request = this.isEdit ? patchSupportMailgunTemplateMetadata(this.templateId, payload) : postSupportMailgunTemplate(payload)
        const { data } = await request
        this.$emit('saved', data)
        this.close()
      } catch (error) {
        this.error = error.response?.data?.details || 'Unable to save this template.'
      } finally { this.saving = false }
    },
    close() { this.$modal.hide(this.modalName) },
    focusFirstField() { this.$nextTick(() => this.$refs.name?.$el?.focus()) }
  }
}
</script>

<style lang="scss" scoped>
.mailgun-template-details { color: $ui-text; font-family: $ui-font-body; }
.mailgun-template-details :deep(.vm--modal) { border-radius: $ui-overlay-radius !important; }
.mailgun-template-details__header { border-radius: $ui-overlay-radius $ui-overlay-radius 0 0; }
.mailgun-template-details__state { display: grid; min-height: 240px; place-items: center; }
.mailgun-template-details__form { display: grid; gap: $ui-space-20; padding: $ui-space-24; }
.mailgun-template-details__grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: $ui-space-16; }
.mailgun-template-details__form ::v-deep textarea { min-height: 96px; resize: vertical; }
.mailgun-template-details__active { display: flex; align-items: center; justify-content: space-between; gap: $ui-space-16; padding: $ui-space-12 $ui-space-16; border: 1px solid $ui-line; border-radius: $ui-panel-radius; background: $ui-surface-soft; }
.mailgun-template-details__active span { display: grid; gap: $ui-space-4; }
.mailgun-template-details__active strong { color: $ui-ink; }
.mailgun-template-details__active small { color: $ui-muted; font-size: $ui-font-size-metadata; }
.mailgun-template-details__error { margin: 0; color: $ui-red; font-size: $ui-font-size-body; }
.mailgun-template-details__footer { display: flex; justify-content: flex-end; gap: $ui-space-12; margin: 0 (-$ui-space-24) (-$ui-space-24); padding: $ui-space-16 $ui-space-24; border-top: 1px solid $ui-line; border-radius: 0 0 $ui-overlay-radius $ui-overlay-radius; background: $ui-surface; }
@media (max-width: 620px) {
  .mailgun-template-details__form { padding: $ui-space-16; }
  .mailgun-template-details__grid { grid-template-columns: 1fr; }
  .mailgun-template-details__footer { margin: 0 (-$ui-space-16) (-$ui-space-16); padding-right: $ui-space-16; padding-left: $ui-space-16; }
}
</style>
